import {
  Button,
  ClickAwayListener,
  List,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import { ArrowDropDown, MoreHoriz } from '@material-ui/icons';
import { FunnelDetails } from 'api-clients/monolith';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import useAnchorState from 'hooks/useAnchorState';
import { usePostHog } from 'hooks/usePostHog';
import { dispatchOpenCue } from 'shared/dispatch-copilot-command';

import { CUE_VISUALIZE_WORKFLOW_CTA } from './analytics-events';
import { messages } from './messages';
import { OpeningsMenu } from './OpeningsMenu';
import { RenameWorkflowModal } from './RenameWorkflowModal';
import { useStyles } from './styles';

type WorkflowUpdateHandler = (newTitle: string, newDescription: string) => void;

export interface WorkflowNavBarProps {
  applicantCount: number;
  workflowFunnels: FunnelDetails['workflow_funnels'];
  workflowTitle: string;
  workflowDescription?: string;
  workflowId: number;
  workflowExternalId: string;
  onWorkflowUpdate: WorkflowUpdateHandler;
}

export const WorkflowNavBar: VFC<WorkflowNavBarProps> = ({
  workflowFunnels,
  workflowExternalId,
  workflowTitle,
  workflowDescription,
  onWorkflowUpdate,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const posthog = usePostHog();
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const openingsMessage =
    workflowFunnels.length === 1
      ? messages.associatedOpening
      : messages.associatedOpenings;

  const whoami = useSelector(makeSelectWhoami());
  // A product-capability gate, not a permission: the item is hidden when
  // either flag is off (constraint-anti-pattern-guard visibility rules).
  const isVisualizeInCueEnabled = Boolean(
    whoami?.feature_flags?.['hire-cue-cta'] &&
    whoami?.feature_flags?.['cue-mermaid-diagrams'],
  );

  const handleToggle = () => {
    if (!open && isVisualizeInCueEnabled) {
      posthog.capture(CUE_VISUALIZE_WORKFLOW_CTA.viewEvent, {
        cta_id: CUE_VISUALIZE_WORKFLOW_CTA.ctaId,
      });
    }
    setOpen(prevOpen => !prevOpen);
  };

  const {
    anchorEl: openingsAnchorEl,
    toggle: toggleOpenings,
    handleClose: handleOpeningsToggleClose,
  } = useAnchorState();

  const handleClose = (event: React.MouseEvent<Document>) => {
    // ClickAwayListener only wraps the MenuList, not the toggle Button (a
    // Popper sibling anchored via `anchorRef`) -- so from its perspective a
    // click on the toggle is itself an "away" click, and it fires its
    // document-level listener for the very click that just opened the menu.
    // Ignoring clicks that land on the anchor leaves the toggle's own
    // onClick (handleToggle) as the sole owner of that click's effect on
    // `open`, matching MUI's documented Popper + ClickAwayListener pattern.
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as Node)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleRenameWorkflow = () => {
    setOpen(false);

    setRenameModalVisible(true);
  };

  const handleVisualizeInCue = () => {
    setOpen(false);
    posthog.capture(CUE_VISUALIZE_WORKFLOW_CTA.clickEvent, {
      cta_id: CUE_VISUALIZE_WORKFLOW_CTA.ctaId,
    });
    // The workflow identity travels through the existing ephemeral-context
    // channel (useWxCopilotWorkflowContext), never through the prompt text.
    dispatchOpenCue({
      message: intl.formatMessage(messages.visualizeWorkflowPrompt),
      openMethod: CUE_VISUALIZE_WORKFLOW_CTA.ctaId,
    });
  };

  return (
    <nav aria-label={intl.formatMessage(messages.workflowEditorNavigation)}>
      <List disablePadding className={styles.navBar} component="ol">
        <li>
          <Button onClick={toggleOpenings} color="inherit" disableRipple>
            <Typography variant="h4">
              <FormattedMessage
                {...openingsMessage}
                values={{ count: workflowFunnels.length }}
              />
            </Typography>
            <ArrowDropDown />
          </Button>
          {openingsAnchorEl && (
            <OpeningsMenu
              anchorEl={openingsAnchorEl}
              onClose={handleOpeningsToggleClose}
              workflowExternalId={workflowExternalId}
            />
          )}
        </li>
        <li>
          <Typography variant="h3" color="inherit">
            {workflowTitle}
          </Typography>
        </li>
        <li>
          <Button
            ref={anchorRef}
            aria-controls={open ? 'workflow-menu' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            color="inherit"
            disableRipple
          >
            <MoreHoriz />
          </Button>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            className={styles.menu}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  <MenuItem onClick={handleRenameWorkflow}>
                    <FormattedMessage {...messages.renameWorkflowMenu} />
                  </MenuItem>
                  {isVisualizeInCueEnabled && (
                    <MenuItem onClick={handleVisualizeInCue}>
                      <FormattedMessage {...messages.visualizeWorkflowInCue} />
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
          <RenameWorkflowModal
            onWorkflowUpdate={onWorkflowUpdate}
            renameModalVisible={renameModalVisible}
            setRenameModalVisible={setRenameModalVisible}
            workflowTitle={workflowTitle}
            workflowDescription={workflowDescription}
          />
        </li>
      </List>
    </nav>
  );
};
